import React from "react";
import type { CampaignReportResponse, CampaignSummary } from "../../types";
import type { ExportTemplateModule } from "../types";
import { coerceNumber, formatCurrency, formatNumber } from "../utils";

interface CampaignTableModuleProps {
  data: CampaignReportResponse;
  module: ExportTemplateModule<"campaign-table">;
}

export const CampaignTableModule: React.FC<CampaignTableModuleProps> = ({
  data,
  module,
}) => {
  const limit = module.props?.limit || 10;
  const campaigns = [...data.campaigns];

  const sorted = campaigns.sort(
    (a, b) => coerceNumber(b.total_sales) - coerceNumber(a.total_sales),
  );

  const top = sorted.slice(0, Math.ceil(limit / 2));
  const bottom = sorted.length > limit
    ? sorted.slice(-Math.floor(limit / 2))
    : [];

  if (!campaigns.length) {
    return (
      <section>
        {module.title && (
          <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
            {module.title}
          </div>
        )}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_20px_45px_rgba(15,23,42,0.08)] p-6 text-slate-500 text-sm">
          No campaign data available.
        </div>
      </section>
    );
  }

  return (
    <section>
      {module.title && (
        <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
          {module.title}
        </div>
      )}
      <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_25px_50px_rgba(15,23,42,0.08)] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Campaign
              </th>
              <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Sales
              </th>
              <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Spend
              </th>
              <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                ROAS
              </th>
              <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Clicks
              </th>
            </tr>
          </thead>
          <tbody>
            {top.map((campaign) => (
              <CampaignRow key={campaign.campaign_name} campaign={campaign} />
            ))}
            {bottom.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-2 text-xs text-slate-400 uppercase tracking-wider bg-slate-50/40 border-y border-slate-100"
                  >
                    Bottom performers
                  </td>
                </tr>
                {bottom.map((campaign) => (
                  <CampaignRow
                    key={campaign.campaign_name}
                    campaign={campaign}
                    muted
                  />
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

function CampaignRow({
  campaign,
  muted,
}: {
  campaign: CampaignSummary;
  muted?: boolean;
}) {
  const roas = coerceNumber(campaign.roas);
  return (
    <tr
      className={`border-b border-slate-50 ${muted ? "opacity-60" : ""}`}
    >
      <td className="px-5 py-2.5 text-slate-900 font-medium truncate max-w-[400px]">
        {campaign.campaign_name}
      </td>
      <td className="px-5 py-2.5 text-right text-slate-700">
        {formatCurrency(campaign.total_sales, { maximumFractionDigits: 0 })}
      </td>
      <td className="px-5 py-2.5 text-right text-slate-700">
        {formatCurrency(campaign.total_spend, { maximumFractionDigits: 0 })}
      </td>
      <td className="px-5 py-2.5 text-right">
        <span
          className={`font-semibold ${
            roas >= 3 ? "text-emerald-600" : roas >= 1 ? "text-slate-700" : "text-rose-500"
          }`}
        >
          {roas.toFixed(2)}x
        </span>
      </td>
      <td className="px-5 py-2.5 text-right text-slate-700">
        {formatNumber(campaign.total_clicks, { maximumFractionDigits: 0 })}
      </td>
    </tr>
  );
}
